// Pure pick logic for the offline company picker (spec 2026-11-02-offline-company-picker §3, §4.3).
// Three names, never swapped: company_env ('ofhgalil'), key ('ofa'), mirror_env ('ofhgalil.ofa').
// A choice is {company_env, key, mirror_env, title, name, noUpload}.

// The shell's `companyPick` while the picker loads (spec §4.3, ruling P16): a page waits on it -- never acts as
// a connected installation (null) nor follows a pick (it has no `pick` and no `tenantId`). Read by shape, not
// identity: every page is its own esbuild bundle, with its own copy of this module and of PICK_PENDING.
export const PICK_PENDING = Object.freeze({ pending: true });
export const pickPending = (cp) => cp?.pending === true;

export function choices(companies) {
  const out = [];
  for (const c of companies || []) {
    const envs = c.environments || [];
    if (!envs.length) {
      out.push({ company_env: c.company_env, key: null, mirror_env: null, title: c.title, name: null, noUpload: true });
    }
    for (const e of envs) {
      out.push({ company_env: c.company_env, key: e.key, mirror_env: e.mirror_env ?? null, title: c.title,
                 name: e.name, noUpload: e.mirror_env == null });
    }
  }
  return out;
}

export const findChoice = (companies, companyEnv, key) =>
  choices(companies).find((c) => c.company_env === companyEnv && c.key === (key ?? null)) || null;

export const choiceByMirrorEnv = (companies, mirrorEnv) =>
  (mirrorEnv && choices(companies).find((c) => c.mirror_env === mirrorEnv)) || null;

export function choiceForCompany(companies, companyEnv) {
  const own = choices(companies).filter((c) => c.company_env === companyEnv);
  return own.find((c) => !c.noUpload) || own[0] || null;
}

export const choiceForRun = (companies, run) =>
  !run ? null
    : run.dump_key ? findChoice(companies, run.env, run.dump_key)
    : choiceForCompany(companies, run.env);

// Spec §4.3: the server's pick, then what this browser remembered (a mirror env, then a company env),
// then the first choice with an upload, then the first choice.
export function restorePick({ server, remembered, companies }) {
  if (server) {
    // A pick stored before its company had environments names no key: that company's newest upload.
    const hit = findChoice(companies, server.company_env, server.key)
      || (server.key == null && choiceForCompany(companies, server.company_env));
    if (hit) return hit;
  }
  if (remembered) {
    const hit = choiceByMirrorEnv(companies, remembered) || choiceForCompany(companies, remembered);
    if (hit) return hit;
  }
  const all = choices(companies);
  return all.find((c) => !c.noUpload) || all[0] || null;
}

// A page filters by this: the composite when uploaded, else the company env -- never another company's.
export const pageEnv = (choice) => (choice ? choice.mirror_env || choice.company_env : undefined);

export function choiceLabel(choice, he) {
  const base = choice.name ? `${choice.title} · ${choice.name}` : choice.title;
  return choice.noUpload ? base + (he ? ' — אין העלאה עדיין' : ' — no upload yet') : base;
}

// Talk (spec §4.4, plan R1): following, Talk is on the pick's composite -- except while an answer streams
// and the pick moved, when it keeps `current` until the answer ends (`waiting`). A pick with no upload, or
// none, leaves Talk on nothing (`noUpload`). Not following, Talk keeps its own company.
export function talkEnv({ follows, pick, sending, current }) {
  if (!follows) return { env: current, waiting: false, noUpload: false };
  const next = pick && !pick.noUpload ? pick.mirror_env : null;
  if (sending && next !== current) return { env: current, waiting: true, noUpload: false };
  return { env: next, waiting: false, noUpload: next === null };
}
